import axios from 'axios';

import createUrlWithParams, { ParamsArray } from 'services/common/createUrlWithParams/createUrlWithParams';

import { DefaultApiResponse, PaginatedApiResponse, PaginatedDataDto } from '../_types';
import type { ExerciseId } from './exercises.branded';
import {
  parseClassicExerciseForUI,
  parseExerciseIsDoneRequestForBE,
  parseExerciseListPageForUI,
  parseExerciseListRowForUI,
  parseExerciseMarkRequestForBE,
  parseExercisePrintForUI,
  parseExerciseUserDataForUI,
  parseExerciseUserDataUpdateForBE,
  parseExerciseUserMarkDetailsForUI,
  parseExercisesForUI,
  parseSheetsCatalogForUI,
  parseTagCategoriesForUI,
} from './exercises.parsers';
import type {
  ClassicExercise,
  ClassicExerciseSectionNames,
  Exercise,
  ExerciseDto,
  ExerciseIsDoneRequest,
  ExerciseListPage,
  ExerciseListQuery,
  ExerciseListRow,
  ExerciseListRowDto,
  ExerciseMarkRequest,
  ExercisePrint,
  ExercisePrintDto,
  ExerciseTagCategory,
  ExerciseTagCategoryDto,
  ExerciseUserData,
  ExerciseUserDataDto,
  ExerciseUserDataUpdate,
  ExerciseUserMarkDetails,
  ExerciseUserMarkDto,
  SheetsCatalog,
  SheetsExercisesQuery,
  TagCategoriesQuery,
} from './exercises.types';

const endpoints = {
  exercises: 'exercises/exercises/',
  print: 'exercises/exercises/print/',
  tagCategories: 'exercises/tag-categories/',
  sheets: 'exercises/sheets/',
  sheetsExercises: 'exercises/sheets/exercises/',
};

type GetSingleExerciseQuery = () => Promise<Exercise>;
type GetClassicExerciseQuery = () => Promise<ClassicExercise>;
type MarkExerciseMutation = (data: ExerciseMarkRequest) => Promise<ExerciseUserMarkDetails>;
type SetIsDoneMutation = (data: ExerciseIsDoneRequest) => Promise<ExerciseUserMarkDetails>;
type UpdateUserDataMutation = (data: ExerciseUserDataUpdate) => Promise<ExerciseUserData>;
type GetExerciseListQuery = () => Promise<ExerciseListPage>;
type GetTagCategoriesQuery = () => Promise<ExerciseTagCategory[]>;
type GetPrintExercisesQuery = () => Promise<ExercisePrint[]>;
type GetSheetsCatalogQuery = () => Promise<SheetsCatalog>;
type GetSheetsExercisesQuery = () => Promise<ExerciseListRow[]>;

const exercisePath = (exerciseId: ExerciseId) => `${endpoints.exercises}${exerciseId}/`;

const toOptionalNumber = (value: number | string | null | undefined): number | undefined => {
  if (value == null || value === '' || value === 'all') return undefined;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? undefined : numeric;
};

export const buildRepeatableParams = (query: {
  tags?: (number | string)[] | null;
  curriculum?: string[] | null;
  marks?: string[] | null;
  isDone?: string[] | null;
  indexes?: number[] | null;
  orderBy?: string | string[] | null;
}): ParamsArray[] => {
  const arrays: ParamsArray[] = [];
  if (query.tags?.length) {
    arrays.push({ paramKey: 'tags', array: query.tags.map(tag => Number(tag)).filter(tag => !Number.isNaN(tag)) });
  }
  if (query.curriculum?.length) {
    arrays.push({ paramKey: 'curriculum', array: query.curriculum });
  }
  if (query.marks?.length) {
    arrays.push({ paramKey: 'marks', array: query.marks });
  }
  if (query.isDone?.length) {
    const isDone = query.isDone.map(value => {
      if (value == null || value === '' || value === 'null') {
        return 'unset';
      }
      return String(value);
    });
    arrays.push({ paramKey: 'is_done', array: isDone });
  }
  if (query.indexes?.length) {
    arrays.push({ paramKey: 'indexes', array: query.indexes });
  }
  if (query.orderBy) {
    const orderBy = Array.isArray(query.orderBy) ? query.orderBy : [query.orderBy];
    if (orderBy.length) {
      arrays.push({ paramKey: 'order_by', array: orderBy });
    }
  }
  return arrays;
};

const exercisesApi = {
  getSingle:
    (exerciseId: ExerciseId): GetSingleExerciseQuery =>
    async () => {
      const response = await axios.get<GetSingleExerciseQuery, DefaultApiResponse<ExerciseDto>>(exercisePath(exerciseId));
      return parseExercisesForUI(response.data);
    },

  /** Classic browse viewer shape (files array + section/subsection objects). */
  getClassic:
    (exerciseId: ExerciseId, names?: ClassicExerciseSectionNames): GetClassicExerciseQuery =>
    async () => {
      const response = await axios.get<GetClassicExerciseQuery, DefaultApiResponse<ExerciseDto>>(exercisePath(exerciseId));
      return parseClassicExerciseForUI(response.data, names);
    },

  getList:
    (query: ExerciseListQuery): GetExerciseListQuery =>
    async () => {
      const response = await axios.get<GetExerciseListQuery, PaginatedApiResponse<ExerciseListRowDto>>(
        createUrlWithParams(
          endpoints.exercises,
          {
            subject: query.subject,
            section: toOptionalNumber(query.section),
            subsection: toOptionalNumber(query.subsection),
            limit: query.limit,
            offset: query.offset,
          },
          buildRepeatableParams(query),
        ),
      );
      return parseExerciseListPageForUI(response.data as PaginatedDataDto<ExerciseListRowDto>);
    },

  getTagCategories:
    (query: TagCategoriesQuery): GetTagCategoriesQuery =>
    async () => {
      const response = await axios.get<GetTagCategoriesQuery, DefaultApiResponse<ExerciseTagCategoryDto[]>>(
        createUrlWithParams(
          endpoints.tagCategories,
          {
            subject: query.subject,
            section: toOptionalNumber(query.section),
            subsection: toOptionalNumber(query.subsection),
          },
          buildRepeatableParams({ tags: query.tags, curriculum: query.curriculum }),
        ),
      );
      return parseTagCategoriesForUI(response.data);
    },

  getPrint:
    (uuids: string[]): GetPrintExercisesQuery =>
    async () => {
      const response = await axios.get<GetPrintExercisesQuery, DefaultApiResponse<ExercisePrintDto[]>>(
        createUrlWithParams(endpoints.print, {}, { paramKey: 'uuid', array: uuids.slice(0, 100) }),
      );
      return (response.data ?? []).map(parseExercisePrintForUI);
    },

  getSheetsCatalog:
    (query: { subject: number; curriculum: string[] }): GetSheetsCatalogQuery =>
    async () => {
      const response = await axios.get<GetSheetsCatalogQuery, DefaultApiResponse<Record<string | number, number[]>>>(
        createUrlWithParams(endpoints.sheets, { subject: query.subject }, { paramKey: 'curriculum', array: query.curriculum }),
      );
      return parseSheetsCatalogForUI(response.data);
    },

  getSheetsExercises:
    (query: SheetsExercisesQuery): GetSheetsExercisesQuery =>
    async () => {
      const response = await axios.get<GetSheetsExercisesQuery, DefaultApiResponse<ExerciseListRowDto[]>>(
        createUrlWithParams(
          endpoints.sheetsExercises,
          {
            subject: query.subject,
            year: query.year,
            month: query.month,
          },
          { paramKey: 'curriculum', array: query.curriculum },
        ),
      );
      return (response.data ?? []).map(parseExerciseListRowForUI);
    },

  mark:
    (exerciseId: ExerciseId): MarkExerciseMutation =>
    async data => {
      const response = await axios.put<MarkExerciseMutation, DefaultApiResponse<ExerciseUserMarkDto>>(
        `${exercisePath(exerciseId)}mark/`,
        parseExerciseMarkRequestForBE(data),
      );
      return parseExerciseUserMarkDetailsForUI(response.data);
    },

  setIsDone:
    (exerciseId: ExerciseId): SetIsDoneMutation =>
    async data => {
      const response = await axios.put<SetIsDoneMutation, DefaultApiResponse<ExerciseUserMarkDto>>(
        `${exercisePath(exerciseId)}is_done/`,
        parseExerciseIsDoneRequestForBE(data),
      );
      return parseExerciseUserMarkDetailsForUI(response.data);
    },

  updateUserData:
    (exerciseId: ExerciseId): UpdateUserDataMutation =>
    async data => {
      const response = await axios.put<UpdateUserDataMutation, DefaultApiResponse<ExerciseUserDataDto>>(
        `${exercisePath(exerciseId)}user_data/`,
        parseExerciseUserDataUpdateForBE(data),
      );
      return parseExerciseUserDataForUI(response.data);
    },
};

export default exercisesApi;
